import { ElephantPresetType } from '@aibees/elephant/types';
import { useBrowsePresetApi } from '@aibees/shared/api';
import { PresetVisibility } from '@aibees/shared/definition';
import { AdvanceTableFilterValue } from '@aibees/shared/features';
import { DURATION_MS } from '@aibees/shared/utils';
import { useMemo } from 'react';

/**
 * Counts for the saved-search panels (View Records → Saved, and the Saved Search
 * settings page).
 *
 * The preset endpoint returns no server-side `metadata` block — unlike the label /
 * export / enrichment browses, whose counts come straight off the response — so we
 * ask for the totals directly: a fixed 2x2 matrix of `size: 1` browses,
 * {Company, People} x {Mine, Team}, carrying the active search text.
 *
 *              PRIVATE (Mine)   INTERNAL (Team)
 *   Company          a                 b
 *   People           c                 d
 *
 * Every number on the panel is a slice of that matrix, which is what keeps them
 * from contradicting each other: both splits partition the same set, so
 * a + b + c + d is the unfiltered total however you add it up. `visibility` is an
 * ownership filter for saved searches (PRIVATE = created by me, INTERNAL = created
 * by a teammate) — see docs/saved-search-presets.md.
 *
 * The four query keys depend only on the search text, so switching type or scope is
 * a pure cache hit; only typing re-fires, and that is already debounced upstream by
 * SearchBarFilter. React Query dedupes across consumers, so mounting this in both
 * the dropdown and the scope tabs still costs four requests, not eight.
 */

const COUNT_STALE_TIME = DURATION_MS.minutes(1);
const COUNT_PAGE = { page: 0, size: 1 } as const;

export interface SavedSearchCountsFilters {
  name?: AdvanceTableFilterValue;
  type?: AdvanceTableFilterValue;
  visibility?: AdvanceTableFilterValue;
}

export interface SavedSearchCounts {
  /** Totals for the type dropdown, narrowed to the active scope tab. */
  byType: { company: number; people: number; all: number } | undefined;
  /** Totals for the All / Mine / Team tabs, narrowed to the active type. */
  byScope: { mine: number; team: number; all: number } | undefined;
  isLoading: boolean;
}

const asText = (value: AdvanceTableFilterValue) =>
  typeof value === 'string' && value ? value : undefined;

function useSavedSearchTotal(
  type: ElephantPresetType,
  visibility: PresetVisibility,
  name?: string
) {
  const { data, isLoading } = useBrowsePresetApi(
    { type, visibility, name, ...COUNT_PAGE },
    { keepPreviousData: true, staleTime: COUNT_STALE_TIME }
  );

  return { total: data?.totalElements, isLoading };
}

export function useSavedSearchCounts(
  filters?: SavedSearchCountsFilters
): SavedSearchCounts {
  const name = asText(filters?.name);
  const type = asText(filters?.type);
  const visibility = asText(filters?.visibility);

  const companyMine = useSavedSearchTotal(
    ElephantPresetType.COMPANY_SAVED_SEARCH,
    PresetVisibility.PRIVATE,
    name
  );
  const companyTeam = useSavedSearchTotal(
    ElephantPresetType.COMPANY_SAVED_SEARCH,
    PresetVisibility.INTERNAL,
    name
  );
  const peopleMine = useSavedSearchTotal(
    ElephantPresetType.PEOPLE_SAVED_SEARCH,
    PresetVisibility.PRIVATE,
    name
  );
  const peopleTeam = useSavedSearchTotal(
    ElephantPresetType.PEOPLE_SAVED_SEARCH,
    PresetVisibility.INTERNAL,
    name
  );

  const isLoading =
    companyMine.isLoading ||
    companyTeam.isLoading ||
    peopleMine.isLoading ||
    peopleTeam.isLoading;

  return useMemo<SavedSearchCounts>(() => {
    const matrix = [
      companyMine.total,
      companyTeam.total,
      peopleMine.total,
      peopleTeam.total,
    ];

    // Stay undefined until every cell has landed, so a pending request can't
    // render a convincing 0.
    if (matrix.some((cell) => cell === undefined)) {
      return { byType: undefined, byScope: undefined, isLoading };
    }

    const [cMine, cTeam, pMine, pTeam] = matrix as number[];

    const inScope = (mine: number, team: number) => {
      if (visibility === PresetVisibility.PRIVATE) return mine;
      if (visibility === PresetVisibility.INTERNAL) return team;
      return mine + team;
    };

    const company = inScope(cMine, cTeam);
    const people = inScope(pMine, pTeam);

    // `type` is a comma-joined list when "All Types" is selected, and absent
    // before the table has applied its initial filters — both mean "every type".
    const hasCompany =
      !type || type.includes(ElephantPresetType.COMPANY_SAVED_SEARCH);
    const hasPeople =
      !type || type.includes(ElephantPresetType.PEOPLE_SAVED_SEARCH);

    const mine = (hasCompany ? cMine : 0) + (hasPeople ? pMine : 0);
    const team = (hasCompany ? cTeam : 0) + (hasPeople ? pTeam : 0);

    return {
      byType: { company, people, all: company + people },
      byScope: { mine, team, all: mine + team },
      isLoading,
    };
  }, [
    companyMine.total,
    companyTeam.total,
    peopleMine.total,
    peopleTeam.total,
    isLoading,
    type,
    visibility,
  ]);
}
